import { NotFoundElementDataType } from '@/components/sections/builder/elements/body/not-found/type';
import { ElementProps } from '@/components/sections/builder/types';
import { WrapperChild } from '@/components/sections/builder/wrapper';
import { cn, hexToRgba } from '@/lib/utils';
import { useBuilderStore } from '@/stores/builder';
import React from 'react';
import { shallow } from 'zustand/shallow';

export default function NotFoundElementContent({ element, theme, preview }: ElementProps) {
  const childs = element.childs;
  const data = element.data as NotFoundElementDataType;

  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
    }),
    shallow
  );

  const themeData = preview ? builder.theme : theme;

  const alignmentToFlexPosition = (position: string = 'middleCenter') => {
    const map: Record<string, string> = {
      topLeft: 'items-start justify-start',
      topCenter: 'items-start justify-center',
      topRight: 'items-start justify-end',
      middleLeft: 'items-center justify-start',
      middleCenter: 'items-center justify-center',
      middleRight: 'items-center justify-end',
      bottomLeft: 'items-end justify-start',
      bottomCenter: 'items-end justify-center',
      bottomRight: 'items-end justify-end',
    };

    return map[position] || 'items-center justify-center';
  };

  return (
    <>
      <section
        className={cn(
          'relative flex min-h-[calc(100vh_-_350px)] w-full flex-col',
          data.background?.type === 'color' && 'background-color',
          data.background?.type === 'image' && 'background-image',
          data.background?.backgroundLayout === 'grid' && 'theme_layout_page_width mx-auto'
        )}
        style={
          {
            '--text-color': data.colorScheme?.text || '#000000',
            '--text-secondary-color': data.colorScheme?.textSecondary || '#666666',
            '--bg-color': data.colorScheme?.background || '#ffffff',
            '--bg-overlay':
              `${hexToRgba(data.overlay, (data?.overlayOpacity ?? 0) / 100)}` ||
              'rgba(0, 0, 0, 0.5)',
            '--btn-label': data.colorScheme?.btnSolidlabel || '#000000',
            '--btn-bg': data.colorScheme?.btnSolidBackgroud || '#ffffff',
            '--btn-outline': data.colorScheme?.btnOutline || '#000000',
          } as React.CSSProperties
        }
      >
        {data.overlay && <div className='absolute inset-0 bg-[--bg-overlay]' />}

        <div
          className={cn(
            'relative z-10 flex w-full flex-1',
            alignmentToFlexPosition(data.alignment)
          )}
        >
          <div
            className='theme_layout_page_width container w-full px-3 pb-[var(--padding-bottom)] pt-[var(--padding-top)] duration-100 md:px-4 lg:p-[var(--padding)]'
            style={
              {
                '--padding': `${data.padding?.top || 0}px ${data.padding?.right || 0}px ${
                  data.padding?.bottom || 0
                }px ${data.padding?.left || 0}px`,
                '--padding-top': `${data.padding?.top || 0}px`,
                '--padding-bottom': `${data.padding?.bottom || 0}px`,
              } as React.CSSProperties
            }
          >
            <div
              className={cn(
                'my-2 flex w-full flex-col gap-4',
                data.alignment?.includes('Left') && 'text-left',
                (data.alignment?.includes('Center') || !data.alignment) && 'text-center',
                data.alignment?.includes('Right') && 'text-right'
              )}
            >
              {(childs || []).map((child) => (
                <WrapperChild
                  key={child.id}
                  child={{
                    ...child,
                    parentData: data,
                    theme: themeData,
                  }}
                  parentType={element.type}
                  preview={preview}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <style jsx>
        {`
          .background-color {
            background-color: ${data.background?.backgroundColor !== ''
              ? data.background?.backgroundColor
              : data.colorScheme?.background};
            --tw-bg-opacity: ${data.background?.backgroundOpacity};
          }

          .background-image {
            background-image: url(${data.background?.backgroundImage || ''});
            background-repeat: no-repeat;
            background-size: cover; /* default */
            background-position: center; /* default */
            background-attachment: ${data.background?.backgroundAttachment || 'scroll'};
          }

          /* Mobile default sudah cover center */

          /* Tablet */
          @media (min-width: 640px) {
            .background-image {
              background-size: cover;
              background-position: center top;
            }
          }

          /* Desktop */
          @media (min-width: 1024px) {
            .background-image {
              background-size: cover;
              background-position: center;
            }
          }
        `}
      </style>
    </>
  );
}
